/**
 * Domain redirect utilities for cross-domain authentication
 * Extracts domain prefix from JWT tokens and builds cross-domain URLs
 */

export interface TokenPayload {
  [key: string]: unknown;
}

/**
 * Extracts and normalizes the domain prefix from a JWT token payload
 * @param payload - The decoded JWT token payload
 * @param claimKey - The primary claim key to look for (e.g., 'custom:domain_prefix')
 * @param fallbackClaimKey - The fallback claim key if primary is not found
 * @returns The normalized domain prefix as a lowercase string, or empty string if not found
 */
export function extractDomainPrefixFromToken(
  payload: TokenPayload,
  claimKey: string,
  fallbackClaimKey: string
): string {
  const hasPrimary = Object.prototype.hasOwnProperty.call(payload, claimKey);
  const hasFallback = !hasPrimary && Object.prototype.hasOwnProperty.call(payload, fallbackClaimKey);

  const rawTokenValue = hasPrimary ? payload[claimKey] : hasFallback ? payload[fallbackClaimKey] : '';

  // Handle string values
  if (typeof rawTokenValue === 'string') {
    return rawTokenValue.toLowerCase();
  }

  // Handle array values (take first element if it's a string)
  if (Array.isArray(rawTokenValue) && typeof rawTokenValue[0] === 'string') {
    return rawTokenValue[0].toLowerCase();
  }

  return '';
}

/**
 * Builds a cross-domain URL by replacing the domain prefix in the current location
 * @param targetPrefix - The target domain prefix to redirect to
 * @param currentLocation - The current window.location object (or compatible interface)
 * @returns The complete URL with the new domain prefix
 */
export function buildCrossDomainUrl(
  targetPrefix: string,
  currentLocation: Pick<Location, 'hostname' | 'port' | 'protocol' | 'pathname' | 'search' | 'hash'>
): string {
  let newHost: string;

  // Handle localhost development environment
  if (currentLocation.hostname.endsWith('localhost')) {
    const port = currentLocation.port || '3000';
    newHost = `${targetPrefix}.localhost:${port}`;
  } else {
    // Handle production domains
    const hostParts = currentLocation.hostname.split('.');
    hostParts[0] = targetPrefix;
    newHost = hostParts.join('.');

    // Add port if present
    if (currentLocation.port) {
      newHost += `:${currentLocation.port}`;
    }
  }

  const { protocol, pathname, search, hash } = currentLocation;
  return `${protocol}//${newHost}${pathname}${search}${hash}`;
}

export const getCurrentHostPrefix = (): string => {
  const host = window.location.hostname;
  const [first = ''] = host.split('.');
  return first;
};

export const buildRedirectUrl = (
  targetPrefix: string,
  path: string = '/login',
  params?: Record<string, string | boolean | number>
): string => {
  const { protocol, host } = window.location;
  const parts = host.split('.');
  if (parts.length > 0) parts[0] = targetPrefix;
  const newHost = parts.join('.');

  // Ensure redirected flag is present as a fallback
  const incoming: Record<string, string> = Object.fromEntries(
    Object.entries(params ?? {}).map(([k, v]) => [k, String(v)])
  );
  const merged: Record<string, string> = 'redirected' in incoming ? incoming : { redirected: 'true', ...incoming };

  const qs = new URLSearchParams(merged);
  const search = qs.toString();

  return `${protocol}//${newHost}${path}${search ? `?${search}` : ''}`;
};

export const getRedirectReason = (loc: Pick<Location, 'search'> = window.location): string | null => {
  const params = new URLSearchParams(loc.search || '');
  const val = params.get('redirected');
  return val ?? (params.has('redirected') ? 'true' : null);
};

export const hasRedirected = (loc: Pick<Location, 'search'> = window.location): boolean =>
  getRedirectReason(loc) !== null;
